import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageStatut from '../../composants/PageStatut/PageStatut';
import Button from '../../composants/Button/Button';
import { getEspaceCible } from '../../utils/espace';

/**
 * Pages d'etat / erreur Centralis (charte conservee, composants existants reutilises).
 *
 * Couverture des cas cote SPA (l'application React tourne) :
 *  - 404 NotFound        : route inconnue (catch-all)
 *  - 403 AccesRefuse     : droits insuffisants pour la page
 *  - 401 SessionExpiree  : non connecte / session expiree
 *  - 500 ErreurServeur   : crash de rendu (via ErrorBoundary) ou erreur technique
 *  - 503 MaintenanceApp  : maintenance applicative "douce" (l'app repond encore)
 *
 * La VRAIE maintenance de mise en prod (l'app React est coupee) est servie par la
 * page statique public/maintenance.html via le proxy — voir docker-compose.maintenance.yml.
 */

// Bouton principal : "mon espace" si l'utilisateur est connecte, sinon l'accueil.
const BoutonEspace: React.FC = () => {
  const navigate = useNavigate();
  const { url, label } = getEspaceCible();
  return <Button value={label} variant="primary" onClick={() => navigate(url)} />;
};

export const NotFound: React.FC = () => (
  <PageStatut
    code="404"
    titre="Page introuvable"
    message="La page que vous recherchez n'existe pas ou a été déplacée."
    actions={<BoutonEspace />}
  />
);

export const AccesRefuse: React.FC = () => (
  <PageStatut
    code="403"
    titre="Accès refusé"
    message="Vous n'avez pas les droits nécessaires pour accéder à cette page."
    actions={<BoutonEspace />}
  />
);

export const SessionExpiree: React.FC = () => {
  const navigate = useNavigate();
  return (
    <PageStatut
      code="401"
      titre="Connexion requise"
      message="Votre session a expiré ou vous n'êtes pas connecté. Veuillez vous reconnecter pour continuer."
      actions={(
        <>
          <Button value="Se connecter" variant="primary" onClick={() => navigate('/login')} />
          <Button value="Accueil" variant="secondary" onClick={() => navigate('/')} />
        </>
      )}
    />
  );
};

/**
 * 500 — rendue par l'ErrorBoundary. On n'utilise PAS useNavigate ici : un crash de
 * routing rendrait le hook indisponible. On retombe sur des liens "durs" (rechargement).
 */
export const ErreurServeur: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => {
  const { url, label } = getEspaceCible();
  return (
    <PageStatut
      code="500"
      avecNavbar={false}
      titre="Une erreur est survenue"
      message="Un incident technique empêche l'affichage de cette page. Si le problème persiste, contactez l'assistance Centralis."
      actions={(
        <>
          <Button value="Réessayer" variant="primary" onClick={onRetry ?? (() => window.location.reload())} />
          <Button value={label} variant="secondary" type="link" linkHref={url} targetBlank={false} />
        </>
      )}
    />
  );
};

export const MaintenanceApp: React.FC = () => (
  <PageStatut
    variant="maintenance"
    code="503"
    titre="Maintenance en cours"
    message={(
      <>
        La plateforme Centralis est momentanément indisponible pour une opération de maintenance.
        <br />
        Merci de revenir dans quelques instants.
      </>
    )}
  />
);
